"use client";

import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from "react";
import { authApi, type AuthResponse, type LoginRequest } from "@/lib/api";

interface AuthUser {
  id: number;
  email: string;
  fullName: string;
  role: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAdmin: boolean;
  login: (request: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = "sekanipay_access_token";
const REFRESH_KEY = "sekanipay_refresh_token";
const USER_KEY = "sekanipay_user";

function getStored<T>(key: string): T | null {
  if (typeof window === "undefined") return null;
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch {
    return null;
  }
}

function setStored(key: string, value: unknown) {
  localStorage.setItem(key, JSON.stringify(value));
}

function clearStored() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

function saveAuthData(data: AuthResponse) {
  setStored(TOKEN_KEY, data.accessToken);
  setStored(REFRESH_KEY, data.refreshToken);
  setStored(USER_KEY, data.user);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Hydrate from localStorage on mount
  useEffect(() => {
    const storedUser = getStored<AuthUser>(USER_KEY);
    if (storedUser) {
      setUser(storedUser);
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (request: LoginRequest) => {
    const data = await authApi.login(request);
    saveAuthData(data);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = getStored<string>(REFRESH_KEY);
    if (refreshToken) {
      await authApi.logout(refreshToken).catch(() => {});
    }
    clearStored();
    setUser(null);
  }, []);

  const getAccessToken = useCallback(() => {
    return getStored<string>(TOKEN_KEY);
  }, []);

  const isAdmin = user?.role === "ADMIN";

  return (
    <AuthContext.Provider value={{ user, isLoading, isAdmin, login, logout, getAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
